import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { FRAME_MS } from '../../engine/games/luck';
import { AI_NAMES, selfDrawWin, type MahjongState } from '../../engine/games/mahjong';
import { meldTiles, tileLong, type Tile } from '../../engine/mahjongTiles';
import { money } from '../../format';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type ActionSpec, type TableProps } from './Frame';
import { MjBack, MjTile } from './MahjongTile';

const NAMES = ['You', ...AI_NAMES];
const WINDS = ['East', 'South', 'West', 'North'];
/** Who throws into the pond: a coloured tab on each tile (mockup H). */
const WHO = ['me', 'lee', 'wu', 'kim'];
/** A ★ redraw: the useless tile shows first, then luck, then the real draw. */
const LOST_MS = 900;
const LUCK_TOTAL_MS = LOST_MS + 1300;
const TILE_BY_LONG = new Map(Array.from({ length: 34 }, (_, t) => [tileLong(t), t] as const));

const seatWind = (s: MahjongState, i: number) => WINDS[(i - s.dealer + 4) % 4]!;

/**
 * The pond's tile width: the largest (up to 21px) at which every discard
 * fits the pond's own box, measured, so it never spills over the legend
 * below it however many tiles are thrown or however small the screen is
 * (user, 2026-09-27). A tile is 1.62 widths tall plus its shadow and its
 * who-tab; the pond spaces them 2px across and 5px down.
 */
function fitPondTile(width: number, height: number, n: number): number {
  for (let w = 21; w > 8; w--) {
    const cols = Math.max(1, Math.floor((width + 2) / (w + 2)));
    const rows = Math.ceil(n / cols);
    if (rows * (w * 1.72 + 3) + (rows - 1) * 5 <= height) return w;
  }
  return 8;
}

/**
 * A box whose tiles shrink, a pixel at a time, until everything in it fits
 * (the side players' melds and flowers: four kongs and a handful of
 * flowers would otherwise run into your hand). Starts back at `max` when
 * `contentKey` changes. Layout effects, so the steps never paint.
 */
function FitTiles({ max, min, contentKey, className, children }: { max: number; min: number; contentKey: string; className: string; children: (w: number) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(max);
  useLayoutEffect(() => setW(max), [contentKey, max]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && w > min && el.scrollHeight > el.clientHeight + 1) setW(w - 1);
  });
  return (
    <div ref={ref} className={className}>
      {children(w)}
    </div>
  );
}

/** Watches an element's size (a callback ref, so it follows the element
 *  as it comes and goes). */
function useBoxSize(): [(el: HTMLElement | null) => void, { w: number; h: number } | null] {
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = (el: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const measure = () => setBox((b) => (b && b.w === el.clientWidth && b.h === el.clientHeight ? b : { w: el.clientWidth, h: el.clientHeight }));
    measure();
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
  };
  return [ref, box];
}

export function MahjongTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const s = run.tables.mahjong?.state as MahjongState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  const uselessDraw = luck ? (TILE_BY_LONG.get(luck.match(/You drew (.+)/)?.[1]?.trim() ?? '') ?? null) : null;
  const elapsed = useAnimClock(snapshot, actions, luck ? LUCK_TOTAL_MS : (anim?.frames.length ?? 0) * FRAME_MS);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, uselessDraw !== null ? LOST_MS : null, () => ({ what: 'A draw that didn’t help is drawn again.', saved: true }), setBurst);

  // The newest discard: whichever pile last grew.
  const seen = useRef<number[]>([]);
  const [newest, setNewest] = useState<{ seat: number; index: number } | null>(null);
  const lengths = s?.seats.map((x) => x.discards.length) ?? [];
  useEffect(() => {
    const before = seen.current;
    const grew = lengths.findIndex((n, i) => n > (before[i] ?? 0));
    if (grew >= 0 && before.length) setNewest({ seat: grew, index: lengths[grew]! - 1 });
    if (lengths.every((n) => n === 0)) setNewest(null);
    seen.current = lengths;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lengths.join(',')]);

  const [pondRef, pondBox] = useBoxSize();
  const [confirmQuit, setConfirmQuit] = useState(false);
  useEffect(() => setConfirmQuit(false), [s?.turn, s?.step, s?.phase]);

  if (!s || s.phase === 'idle' || !s.seats.length) {
    return (
      <>
        <Stage className="mj-stage">
          <div className="mj-intro">
            <p className="felt-hint">Hong Kong mahjong against Lee, Wu and Kim.</p>
            <p>3 fan to win. Your bet is what a 3-fan hand pays, up to 8× at the limit. You pay a win off your own discard, and every self-drawn win. Flowers and seasons are set aside for bonus fan, and a kong draws you a replacement tile.</p>
          </div>
        </Stage>
        <Controls snapshot={snapshot} actions={actions} />
      </>
    );
  }

  const me = s.seats[0]!;
  const myTurn = s.phase === 'play' && s.turn === 0 && s.step === 'discard' && !anim;
  const win = s.phase === 'play' && s.turn === 0 && s.step === 'discard' ? selfDrawWin(s) : null;

  // Your hand, sorted, with the tile you just drew set apart at the end.
  const hand = me.hand;
  const drawnAt = s.phase === 'play' && s.turn === 0 && (s.step === 'discard' || s.step === 'kong') && s.drawn !== null ? hand.lastIndexOf(s.drawn) : -1;
  const shown: { t: Tile; engine: number }[] = hand.map((t, i) => ({ t, engine: i })).filter((x) => x.engine !== drawnAt);
  const drawnTile = drawnAt >= 0 ? { t: hand[drawnAt]!, engine: drawnAt } : null;
  // During a ★ redraw, the useless tile shows in its place until luck steps in.
  const drawnFace = drawnTile && anim && uselessDraw !== null && elapsed < LOST_MS ? uselessDraw : drawnTile?.t;

  const pick = (engine: number) => {
    if (!myTurn) return;
    if (s.cursor === engine && s.confirm) {
      actions.tap(); // second tap: throw it
      return;
    }
    const steps = (engine - s.cursor + hand.length) % hand.length;
    for (let k = 0; k < steps; k++) actions.move('down');
    actions.tap(); // first tap: pick it up
  };
  const lifted = (engine: number) => myTurn && s.confirm && s.cursor === engine;

  // The pond, in throw order: round by round, starting with the dealer.
  const pond: { t: Tile; seat: number; index: number }[] = [];
  const most = Math.max(...s.seats.map((x) => x.discards.length));
  for (let r = 0; r < most; r++) {
    for (let k = 0; k < 4; k++) {
      const seat = (s.dealer + k) % 4;
      const t = s.seats[seat]!.discards[r];
      if (t !== undefined) pond.push({ t, seat, index: r });
    }
  }
  const size = pondBox ? fitPondTile(pondBox.w, pondBox.h, pond.length) : 16;

  const chip = (i: number, vertical = false) => (
    <span className={`seat-chip${s.turn === i && s.phase === 'play' ? ' turn' : ''}${vertical ? ' vertical' : ''}`}>
      {NAMES[i]} · {vertical ? seatWind(s, i)[0] : seatWind(s, i)}
      <span className="count" title="Tiles in hand">{s.seats[i]!.hand.length}</span>
    </span>
  );
  // A kong is drawn the traditional way, as a pung with the fourth tile laid
  // on the middle one, so it takes no more room than a pung; a concealed
  // kong shows its outer tiles face down.
  const melds = (i: number, w: number, stacked: boolean) => [
    ...s.seats[i]!.melds.map((m, k) => {
      const kong = m.kind === 'kong';
      const tiles = kong ? [m.tile, m.tile, m.tile] : meldTiles(m);
      return (
        <span key={k} className={`mj-meld${stacked ? ' stacked' : ''}${kong ? ' kong' : ''}`} aria-label={kong ? `Kong of ${tileLong(m.tile)}${m.open ? '' : ', concealed'}` : undefined}>
          {tiles.map((t, j) => (kong && !m.open && j !== 1 ? <MjBack key={j} w={w} /> : <MjTile key={j} t={t} w={w} />))}
          {kong && <MjTile t={m.tile} w={w} className="kong-top" />}
        </span>
      );
    }),
    ...(s.seats[i]!.flowers?.length
      ? [
          <span key="flowers" className="mj-flowers" aria-label="Flowers and seasons">
            {s.seats[i]!.flowers.map((t) => <MjTile key={t} t={t} w={Math.round(w * 0.8)} />)}
          </span>,
        ]
      : []),
  ];

  const sideKey = (i: number) => JSON.stringify([s.seats[i]!.melds, s.seats[i]!.flowers]);
  const r = s.result;
  const winner = r?.winner ?? null;
  const winnerHand = winner !== null ? [...s.seats[winner]!.hand, ...s.seats[winner]!.melds.flatMap(meldTiles)] : [];

  const extra: ActionSpec[] = [];
  if (win && !anim) extra.push({ id: 'mahjong', label: `Mahjong! (${win.fan} fan)`, primary: true, onClick: actions.back });
  if (s.phase === 'play' && s.step !== 'claim' && s.step !== 'kong' && !win && !anim) {
    extra.push({
      id: 'quit',
      label: confirmQuit ? `Give up? (−${money(s.bet)})` : 'Give up',
      onClick: () => {
        if (!confirmQuit) return setConfirmQuit(true);
        // The engine's give-up page: open the piles, step back to it, tap.
        actions.back();
        actions.move('up');
        actions.tap();
      },
    });
  }

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (s.phase === 'done' && r && !anim) {
    if (r.how === 'giveUp') result = `You gave up the hand · −${money(-r.net)}`;
    else if (r.winner === null) result = 'The wall ran out · nobody pays';
    else {
      const how = r.how === 'selfDraw' ? 'self-drawn' : r.from === 0 ? 'off your discard' : `off ${NAMES[r.from!]}`;
      result = `${r.winner === 0 ? `${s.lucky ? '★ ' : ''}You win` : `${NAMES[r.winner]} wins`}, ${how} · ${r.fan} fan · ${r.net > 0 ? `+${money(r.net)}` : r.net < 0 ? `you pay ${money(-r.net)}` : 'costs you nothing'}`;
    }
    tone = r.net > 0 ? 'win' : r.net < 0 ? 'loss' : '';
  }

  const status = s.phase !== 'play' ? '' : s.step === 'kong' ? 'Kong it? You draw a replacement tile.' : s.step === 'claim' && s.pending ? `${NAMES[s.pending.by]} threw ${tileLong(s.pending.tile)}` : myTurn ? (s.confirm ? 'Tap it again to throw it' : 'Your turn: tap a tile to throw') : s.turn === 0 ? 'Drawing…' : `${NAMES[s.turn]} is playing…`;

  return (
    <>
      <Stage className="mj-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="mj-table">
          <div className="mj-top">
            {chip(2)}
            <div className="mj-melds">{melds(2, 15, false)}</div>
          </div>
          <div className="mj-side left">
            {chip(3, true)}
            <FitTiles max={13} min={7} contentKey={sideKey(3)} className="mj-melds">{(w) => melds(3, w, true)}</FitTiles>
          </div>
          <div className="mj-centre">
            {s.phase === 'done' && winner !== null && !anim ? (
              <div className="mj-reveal">
                <span className="mj-reveal-title">{winner === 0 ? 'Your hand' : `${NAMES[winner]}’s hand`}</span>
                <div className="mj-row">{winnerHand.map((t, i) => <MjTile key={i} t={t} w={19} />)}</div>
                <span className="mj-fan">{r!.names.join(' · ')}</span>
              </div>
            ) : (
              <div className="mj-pond" ref={pondRef} aria-label="Discards">
                {pond.map((p) => (
                  <MjTile
                    key={`${p.seat}-${p.index}`}
                    t={p.t}
                    w={size}
                    className={`who-${WHO[p.seat]}${newest && newest.seat === p.seat && newest.index === p.index ? ' newest' : ''}`}
                    label={`${NAMES[p.seat]} threw ${tileLong(p.t)}`}
                  />
                ))}
              </div>
            )}
            <div className="mj-legend" aria-hidden="true">
              {[3, 2, 1, 0].map((i) => <span key={i} className={`who-${WHO[i]}`}><i />{NAMES[i]}</span>)}
            </div>
            <div className="mj-wall">{s.wall.length} tiles left · East round · you’re {seatWind(s, 0)}</div>
          </div>
          <div className="mj-side right">
            {chip(1, true)}
            <FitTiles max={13} min={7} contentKey={sideKey(1)} className="mj-melds">{(w) => melds(1, w, true)}</FitTiles>
          </div>
          <div className="mj-mine">
            <div className="mj-status">{status}</div>
            {(me.melds.length > 0 || (me.flowers?.length ?? 0) > 0) && <div className="mj-melds mine">{melds(0, 18, false)}</div>}
            <div className="mj-hand">
              {shown.map((x) => (
                <MjTile key={`${x.engine}-${x.t}`} t={x.t} w={38} className={lifted(x.engine) ? 'lifted' : ''} onClick={myTurn ? () => pick(x.engine) : undefined} />
              ))}
              {drawnTile && drawnFace !== undefined && (
                <MjTile key={`drawn-${drawnFace}`} t={drawnFace} w={38} className={`drawn${lifted(drawnTile.engine) ? ' lifted' : ''}`} onClick={myTurn ? () => pick(drawnTile.engine) : undefined} label={`Drew ${tileLong(drawnFace)}`} />
              )}
            </div>
          </div>
        </div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['wait']} extra={extra} onAct={() => setBurst(null)} />
    </>
  );
}

